import { useParams } from "react-router";
import RoundedCard from "@cle/components/RoundedCard";
import {
  useDispensationDocument,
  useDispensationDocumentFile,
} from "@/hooks/useDispensations";
import { formatDanishLongDate } from "@/util/date";
import ManualReviewInfoModal from "./components/ManualReviewInfoModal";
import SecretariatMessageCard from "./components/SecretariatMessageCard";
import UploadedDocumentPreviewCard from "./components/UploadedDocumentPreviewCard";
import { formatLessons } from "../../utils/lessonLimits";

const MISSING_VALUE = "-";

const DispensationDocumentView = () => {
  const { id = "" } = useParams<{ id: string }>();
  const {
    data: dispensation,
    isError: isDispensationError,
    isLoading: isDispensationLoading,
  } = useDispensationDocument(id);
  const {
    data: documentFile,
    isError: isDocumentFileError,
    isLoading: isDocumentFileLoading,
  } = useDispensationDocumentFile(id);
  const dispensationTitle = dispensation?.fileName ?? "Dispensation.pdf";
  // The dispensation model carries no isReported flag; reportedAt is set
  // exactly when the evidence is reported.
  const isReported = Boolean(dispensation?.reportedAt);

  return (
    <div className="cle-course-certificate-document">
      {isDispensationError ? (
        <div className="alert alert-warning" role="alert">
          Dispensationens oplysninger kunne ikke indlæses lige nu.
        </div>
      ) : null}

      <div className="row g-4 align-items-start">
        <div className="col-12 col-xl-6">
          <UploadedDocumentPreviewCard
            certificateTitle={dispensationTitle}
            fileBlob={documentFile}
            isError={isDocumentFileError}
            isLoading={isDocumentFileLoading}
          />
        </div>
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <RoundedCard
              className="cle-course-certificate-document__side-card"
              header="Information"
              action={
                <ManualReviewInfoModal
                  documentType="dispensation"
                  isReported={isReported}
                />
              }
            >
              <dl className="cle-course-certificate-document__info-list mb-0">
                <dt>Dokument</dt>
                <dd>{dispensationTitle}</dd>

                <dt>Type</dt>
                <dd>Dispensation</dd>

                <dt>Antal lektioner</dt>
                <dd>
                  {isDispensationLoading
                    ? "Indlæser"
                    : formatLessons(
                        dispensation?.numberOfLessons,
                        MISSING_VALUE,
                      )}
                </dd>

                <dt>Uploadet</dt>
                <dd>
                  {formatDanishLongDate(dispensation?.createdAt, MISSING_VALUE)}
                </dd>

                {dispensation?.reportedAt ? (
                  <>
                    <dt>Indberettet</dt>
                    <dd>
                      {formatDanishLongDate(
                        dispensation.reportedAt,
                        MISSING_VALUE,
                      )}
                    </dd>
                  </>
                ) : null}
              </dl>
            </RoundedCard>
            <SecretariatMessageCard subjectId={id} isReported={isReported} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DispensationDocumentView;
